/**
 * exportLayoutPdf — the single-sheet PLOT entry point (PDF-1..PDF-4).
 *
 * Since PDF-4 this is a THIN wrapper: it resolves the space to plot, creates a
 * fresh `PDFDocument`, renders ONE sheet into it via {@link renderSheetPage}
 * (the shared page body — paper-space geometry + per-VP clipped model content +
 * the resolved plot area / scale / centering), saves, and returns the
 * `ExportLayoutPdfResult`. The multi-sheet PUBLISH (`publishLayouts`) reuses the
 * same `renderSheetPage` over an ordered sheet list.
 *
 * The exported API + return shape are UNCHANGED from PDF-1/2/3 (other code and
 * tests import them), and a Layout-area sheet is byte-identical to the
 * pre-PDF-4 output (`renderSheetPage` omits the scale/centering overrides for a
 * Layout region).
 *
 * Pure + headless: returns a `Uint8Array` so it is fully unit-testable without
 * a browser / WebGL.
 */

import { PDFDocument } from "pdf-lib";

import {
  defaultPlotSettings,
  type DrawingDocument,
  type DrawingSpace,
  type LayoutPlotSettings,
} from "@dxf-ai-editor/drawing-model";
import type { PlotStyleTable } from "@dxf-ai-editor/acadctb";

import { DEFAULT_PAPER_TOLERANCE_MM } from "./cadPrimitive";
import { type TextPaintResult } from "./textPainter";
import type { ResolveFontOptions } from "./fontResolve";
import {
  renderSheetPage,
  type SheetRenderStats,
  type ViewportPlotStats,
} from "./sheetRenderer";

// Re-export so existing importers of these types from this module keep working.
export type { ViewportPlotStats } from "./sheetRenderer";

/** Options for {@link exportLayoutPdf}. */
export interface ExportLayoutPdfOptions {
  /**
   * Space id to plot. Defaults to the document's `activeSpaceId`. The space
   * must be a layout (paper space); MODEL-tab fallback is handled minimally
   * (see {@link resolveSpace}).
   */
  spaceId?: string;
  /**
   * Plot settings for the layout. Defaults to `defaultPlotSettings()` (A4
   * portrait, Layout area, 1:1). The full DXF PLOTSETTINGS parse is PDF-6.
   */
  settings?: LayoutPlotSettings;
  /** Paper-space tessellation tolerance in mm (default 0.1). */
  toleranceMm?: number;
  /** PDF document title metadata. */
  title?: string;
  /**
   * Font-resolution overrides for the text pass (inject bundled-font bytes / a
   * deterministic loader in headless tests).
   */
  fontResolve?: ResolveFontOptions;
  /**
   * PDF-6.9b — layer NAMES the user un-checked in the "Include Layers" panel
   * of the PDF Options sub-dialog. Threaded into the sheet renderer so
   * omitted-layer geometry is dropped entirely (no OCG, no path / text /
   * hatch / VP-frame operators). Empty / absent → every layer included.
   */
  omitLayers?: ReadonlyArray<string>;
  /**
   * PDF-6.3b — the loaded CTB / STB plot-style table from the PDF Options
   * sub-dialog. Forwarded into the {@link CadConvertContext} so per-entity
   * style resolution applies colour / lineweight / linetype / alpha / do-not-
   * plot overrides. Absent → no overrides (byte-identical to PDF-6.3a output).
   */
  plotStyleTable?: PlotStyleTable;
  /**
   * PDF-6.4 — AutoCAD "Lines merge / Overwrite" PC3 plot setting. When
   * `"merge"`, the exporter wraps every painted run in `/BM /Multiply` so
   * overlapping geometry darkens (parity with AutoCAD's "Lines merge"). Default
   * / absent → `"overwrite"` (PDF Normal blend, byte-identical to pre-6.4
   * output).
   */
  mergeControl?: "overwrite" | "merge";
}

/** Result of a single-sheet PDF export. */
export interface ExportLayoutPdfResult {
  bytes: Uint8Array;
  /** MediaBox dimensions in points (physical paper). */
  mediaBox: { widthPt: number; heightPt: number };
  /** Number of style groups (batched operator blocks) emitted. */
  groupCount: number;
  /** Geometry primitives (paths) emitted: paper-space + per-VP model. */
  pathCount: number;
  /** Recorded text runs (paper-space + per-VP model). */
  textRunCount: number;
  /** Accounting for the text-paint pass (TTF embed / SHX stroke). */
  text: TextPaintResult;
  /** Per-viewport accounting. */
  viewports: ViewportPlotStats;
  /** True when the resolved plot region was empty (blank page §7.3/§7.6). */
  empty?: boolean;
  /** Resolved plot-area note (fallback / empty diagnostics), if any. */
  regionNote?: string;
}

/**
 * Build the PDF bytes for one layout. Async because `PDFDocument.save()` is
 * async (it serialises the object graph).
 */
export async function exportLayoutPdf(
  doc: DrawingDocument,
  options: ExportLayoutPdfOptions = {},
): Promise<ExportLayoutPdfResult> {
  const spaceId = options.spaceId ?? doc.activeSpaceId;
  const space = resolveSpace(doc, spaceId);
  // PDF-6.1: read the per-layout PLOTSETTINGS from the model. Order:
  //   1. explicit `options.settings` override (tests / PrintDialog),
  //   2. the resolved layout's `plotSettings` (the imported / authored page),
  //   3. `defaultPlotSettings()` when the layout truly has none.
  const settings =
    options.settings ?? resolvePlotSettings(doc, space.id);

  const pdf = await PDFDocument.create();
  if (options.title) pdf.setTitle(options.title);

  const stats: SheetRenderStats = await renderSheetPage(pdf, doc, space, {
    settings,
    toleranceMm: options.toleranceMm ?? DEFAULT_PAPER_TOLERANCE_MM,
    fontResolve: options.fontResolve,
    omitLayers: options.omitLayers,
    // PDF-6.3b — forward the loaded plot-style table (CTB / STB) to the sheet
    // renderer so its per-entity style resolution applies the override paths.
    plotStyleTable: options.plotStyleTable,
    // PDF-6.4 — forward the session-level "Lines merge / Overwrite" setting.
    // "merge" wraps every painted run in `/BM /Multiply` so overlapping
    // geometry darkens (AutoCAD PC3 parity); "overwrite" / absent → byte-
    // identical to pre-6.4 output.
    mergeControl: options.mergeControl,
  });

  const bytes = await pdf.save();

  return {
    bytes,
    mediaBox: stats.mediaBox,
    groupCount: stats.groupCount,
    pathCount: stats.pathCount,
    textRunCount: stats.textRunCount,
    text: stats.text,
    viewports: stats.viewports,
    empty: stats.empty,
    regionNote: stats.regionNote,
  };
}

/**
 * Resolve the per-layout {@link LayoutPlotSettings} for a space id (PDF-6.1).
 *
 * The model keys the page setup by the disambiguated space id in
 * `doc.layoutsBySpaceId` (e.g. "layout:Beta (1)"); when a layout was imported
 * without that index (older snapshots) we fall back to matching `doc.layouts`
 * by tab name (the space id minus the "layout:" prefix). When the layout
 * carries no plot settings at all (synthetic / partial in-memory docs) the
 * canonical {@link defaultPlotSettings} A4 page is returned so the page can
 * still be built.
 */
export function resolvePlotSettings(
  doc: DrawingDocument,
  spaceId: string,
): LayoutPlotSettings {
  const byId = doc.layoutsBySpaceId?.[spaceId];
  if (byId?.plotSettings) return byId.plotSettings;
  // Fallback: match by tab name (strip the "layout:" id prefix).
  const tabName = spaceId.startsWith("layout:")
    ? spaceId.slice("layout:".length)
    : spaceId;
  const byName = doc.layouts?.[tabName];
  if (byName?.plotSettings) return byName.plotSettings;
  return defaultPlotSettings();
}

/**
 * Resolve the space to plot. Prefers the requested / active space; falls back
 * to the first layout space, then (MODEL-tab minimal fallback) the model space.
 * The full MODEL-fit-to-paper region (§7.1) is deferred; here we still plot
 * model-space geometry through the same transform so the path is not a hard
 * error.
 */
export function resolveSpace(
  doc: DrawingDocument,
  spaceId: string,
): DrawingSpace {
  const byId = doc.spaces.find((s) => s.id === spaceId);
  if (byId) return byId;
  const layout = doc.spaces.find((s) => s.kind === "layout");
  if (layout) return layout;
  return doc.spaces[0]!;
}
